<template>
  <ModalShell :visible="visible" :z-index="2000" @close="$emit('close')">
    <div class="recommend-dialog">
      <!-- 头部 -->
      <div class="recommend-dialog-header">
        <div class="recommend-dialog-header-left">
          <div class="recommend-dialog-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div>
            <div class="recommend-dialog-title">{{ t('config_recommend') }}</div>
            <div class="recommend-dialog-subtitle">{{ t('config_recommend_desc') }}</div>
          </div>
        </div>
        <div class="mgx_close_btn" @click="$emit('close')">
          <SvgAsset :content="windowCloseSvg" />
        </div>
      </div>
      <!-- 卡片列表 -->
      <div class="recommend-dialog-grid">
        <div class="recommend-dialog-card" v-for="(item, index) in configSet.recommendConfigList.value" :key="index" @click="applyRecommend(item)">
          <!-- 左侧：色条装饰（用 bg_img 作为缩略色块，而非全屏大图） -->
          <div class="recommend-dialog-card-swatch">
            <img :src="item.bg_img" :alt="item.name" />
          </div>
          <!-- 右侧：文字信息 -->
          <div class="recommend-dialog-card-info">
            <span class="recommend-dialog-card-name">{{ item.name }}</span>
            <span class="recommend-dialog-card-sub">{{ item.sub_name }}</span>
          </div>
          <!-- 操作按钮 -->
          <div class="recommend-dialog-card-action">
            <SvgAsset :content="actionDownloadSvg" />
          </div>
        </div>
      </div>
    </div>
  </ModalShell>
</template>

<script setup lang="ts">
import { configSet, configModel } from '@/service/driver/config_set'
import { DialogType, enableDialog } from '../cdialog/dialog'
import i18n from '@/languages/i18n'
import ModalShell from '../common/ModalShell.vue'
import SvgAsset from '../common/SvgAsset.vue'
import actionDownloadSvg from '@/assets/inline-svg/action-download.svg?raw'
import windowCloseSvg from '@/assets/inline-svg/window-close.svg?raw'

const { t } = i18n.global

defineProps<{ visible: boolean }>()

const emit = defineEmits<{
  close: []
  applied: []
}>()

const applyRecommend = async (item: configModel) => {
  await configSet.setConfigPreset(item.name, item.content)
  enableDialog(t('download_config_success'), DialogType.notice, true)
  emit('applied')
  emit('close')
}
</script>

<style lang="less" scoped>
.recommend-dialog {
  width: 560px;
  max-width: 90vw;
  background: var(--glass-panel-bg);
  backdrop-filter: var(--glass-panel-blur);
  border-radius: var(--radius-l);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.recommend-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;

  .recommend-dialog-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .recommend-dialog-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-m);
    background: linear-gradient(135deg, rgba(var(--color-brand-1-rgb), 0.15), rgba(var(--color-brand-2-rgb), 0.08));
    color: var(--brand-color-pure);
    flex-shrink: 0;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .recommend-dialog-title {
    font-size: 16px;
    font-weight: 800;
    color: var(--color-dark);
    letter-spacing: 0.02em;
    line-height: 1.2;
  }

  .recommend-dialog-subtitle {
    font-size: 12px;
    color: var(--color-text-hint);
    margin-top: 2px;
    line-height: 1.3;
  }
}

.recommend-dialog-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 24px 24px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent !important;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: var(--radius-pill);
  }
}

.recommend-dialog-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(var(--color-brand-1-rgb), 0.25);
    box-shadow: 0 8px 24px rgba(var(--color-brand-1-rgb), 0.1);
    transform: translateY(-2px);

    .recommend-dialog-card-action {
      opacity: 1;
      transform: scale(1);
      background: var(--brand-color-pure);
      color: #fff;
    }

    .recommend-dialog-card-swatch {
      transform: scale(1.04);
    }
  }

  .recommend-dialog-card-swatch {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-s);
    overflow: hidden;
    flex-shrink: 0;
    transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .recommend-dialog-card-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .recommend-dialog-card-name {
    font-size: 14px;
    font-weight: 700;
    color: var(--color-dark);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
  }

  .recommend-dialog-card-sub {
    font-size: 11px;
    color: var(--color-text-hint);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  .recommend-dialog-card-action {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-s);
    background: rgba(var(--color-brand-1-rgb), 0.1);
    color: var(--brand-color-pure);
    flex-shrink: 0;
    opacity: 0.6;
    transform: scale(0.92);
    transition: all 0.35s cubic-bezier(0.23, 1, 0.32, 1);

    .svg_asset,
    svg {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
